
























































































































































































import { Component, Prop, Mixins, Inject } from "vue-property-decorator";
import { IUser } from "@/models/users";
import {
  IUserProviderData,
  IUserProviderMethods
} from "@/models/providers/user";
import { IAdminBrand, IBrand } from "@/models/brands";
import RequestPromises from "@/mixins/requestPromises";
import { IUserGroup } from "@/models/userGroups";
import { DataModules } from "@/store/modules/data";
import HasValidation from "@/mixins/HasValidation";
import LoggedUserMixin from "@/mixins/loggedUserMixin";
import { UUID } from "@/data/enums";
import { ILanguage } from "@/models/constants";

interface IBrandCheckable extends IAdminBrand {
  is_checked: boolean;
}

interface Form {
  active: boolean;
  admin: boolean;
  document_language_code: ILanguage["code"];
  enabled_2fa?: boolean;
  interface_language_code: ILanguage["code"];
  name: string;
  phone: string | null;
  phone_code: string | null;
  phone_country_code: string | null;
}

const initialForm = (): Form => {
  return {
    active: true,
    admin: false,
    document_language_code: "en",
    interface_language_code: "en",
    name: "",
    phone: null,
    phone_code: null,
    phone_country_code: null
  };
};

@Component({
  components: {
    "two-fa-switch": () =>
      import("@/components/app/global/auth/TwoFaSwitch.vue"),
    "user-fields": () => import("@/components/app/admin/users/userFields.vue")
  }
})
export default class UserForm extends Mixins(
  RequestPromises,
  HasValidation,
  LoggedUserMixin
) {
  @Inject({ from: "userProvider" })
  public readonly $userProvider!: () => IUserProviderData;

  @Inject({ from: "userMethods" })
  public readonly $userMethods!: IUserProviderMethods;

  @Prop({ required: true })
  public userId!: IUser["id"];

  public userGroups: IUserGroup[] = [];
  public brands: IBrandCheckable[] = [];
  public userBrandGroup: object = {};
  public initialUserBrandGroup: object = {};
  public form: Form = initialForm();
  public initialForm: Form = initialForm();
  public sendingVerificationEmail = false;

  public isLoading = true;
  public isReloading = false;
  public isProcessing = {};
  public requestError = false;

  public async created() {
    this.initBrandGroups();
    this.$bus.$on("admin/userGroups/save", this.reloadData);
    await this.loadData();
    this.isLoading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      if (this.canView) {
        await this.getUser();
      }
      if (this.canListUserGroups) {
        await this.getUserGroups();
      }
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public validateBeforeSubmit() {
    if (!this.canEdit) return;
    this.$set(this.isProcessing, "submit", true);

    this.validate()
      .then(result => {
        if (result) return this.save();
        throw this.$t("_sentence.form_is_not_valid");
      })
      .then(() => {
        this.initialForm = this.$_.cloneDeep(this.form);
        this.initialUserBrandGroup = this.$_.cloneDeep(this.userBrandGroup);
        this.$buefy.toast.open({
          message: this.$t("_sentence.successfully_saved") as string
        });
        if (this.isLoggedUser) {
          this.$store.dispatch("auth/admin/getUser");
        }
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.$delete(this.isProcessing, "submit");
      });
  }

  public save() {
    this.form.document_language_code = this.form.interface_language_code;
    const data = this.$_.clone(this.form);
    if (!this.form.admin && !this.isLoggedUser) {
      this.$_.set(data, "user_brand_group", this.prepareUseBrandGroup());
    }

    return this.$store.dispatch(`data/${DataModules.USERS}/update`, {
      id: this.$userData.user.id,
      data: data
    });
  }

  public revertToInitialState() {
    this.form = this.$_.cloneDeep(this.initialForm);
    this.userBrandGroup = this.$_.cloneDeep(this.initialUserBrandGroup);
  }

  public get $userData() {
    return this.$userProvider();
  }

  public get isFormChanged(): boolean {
    return (
      this.$_.isEqual(this.form, this.initialForm) &&
      this.$_.isEqual(
        this.$_.sortBy(
          this.$_.map(
            this.$_.cloneDeep(this.userBrandGroup),
            (permissions: number[]) => this.$_.sortBy(permissions)
          )
        ),
        this.$_.sortBy(
          this.$_.map(
            this.$_.cloneDeep(this.initialUserBrandGroup),
            (permissions: number[]) => this.$_.sortBy(permissions)
          )
        )
      )
    );
  }

  public toggleBrand(brand: IBrandCheckable): void {
    if (!brand.is_checked) {
      this.$_.set(this.userBrandGroup, brand.id, []);
    }
    this.$forceUpdate();
  }

  public initBrandGroups(): void {
    const brands = this.$_.clone(
      this.$_.filter(this.$store.state.auth.admin.brands, brand => {
        return brand.id !== UUID.ORG;
      }) as IBrandCheckable[]
    );
    this.$set(this, "brands", brands);
    const userBrandGroup = {};
    this.$_.forEach(this.brands, el => {
      this.$_.set(userBrandGroup, el.id, []);
      el.is_checked = false;
    });
    this.$set(this, "initialUserBrandGroup", this.$_.clone(userBrandGroup));
    this.$set(this, "userBrandGroup", this.$_.clone(userBrandGroup));
  }

  public prepareUseBrandGroup() {
    const userBrandGroup: Array<{
      brand_id: IBrand["id"];
      group_id: string;
    }> = [];
    if (this.$_.isEmpty(this.userBrandGroup)) {
      return userBrandGroup;
    }
    this.$_.forEach(Object.keys(this.userBrandGroup), brandId => {
      this.$_.forEach(this.userBrandGroup[brandId], groupId => {
        userBrandGroup.push({
          brand_id: brandId,
          group_id: groupId
        });
      });
    });

    return userBrandGroup;
  }

  public async getUserGroups() {
    this.userGroups = await this.$store.dispatch(
      `data/${DataModules.USER_GROUPS}/list`,
      {
        vm: this,
        storeData: false,
        params: {
          limit: 1000
        }
      }
    );
  }

  public async getUser() {
    this.$_.forEach(this.$userData.user.user_brand_group, el => {
      if (this.userBrandGroup[el.brand_id] === undefined) {
        this.$_.set(this.userBrandGroup, el.brand_id, []);
      }
      this.userBrandGroup[el.brand_id].push(el.group_id);
      const brand = this.$_.find(this.brands, ["id", el.brand_id]);
      if (brand && !this.$_.get(brand, "is_checked", false)) {
        this.$_.set(brand, "is_checked", true);
      }
    });

    this.form = this.$_.pick(this.$userData.user, [
      "name",
      "email",
      "admin",
      "phone",
      "active",
      "phone_code",
      "phone_country_code",
      "enabled_2fa",
      "interface_language_code",
      "document_language_code",
      "verified"
    ]);
    this.$set(this, "initialForm", this.$_.clone(this.form));
    this.$set(
      this,
      "initialUserBrandGroup",
      this.$_.cloneDeep(this.userBrandGroup)
    );

    return Promise.resolve();
  }

  public resendVerification() {
    if (!this.canResendVerificationEmail) {
      return;
    }
    this.sendingVerificationEmail = true;
    this.$store
      .dispatch(`data/${DataModules.USERS}/resendVerification`, {
        data: {
          user_id: this.userId
        }
      })
      .then(() => {
        this.$buefy.toast.open({
          message: this.$t("_.verification_email_sent") as string
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => (this.sendingVerificationEmail = false));
  }

  public sendPasswordReset() {
    if (!this.canResetPassword) return;
    this.$set(this.isProcessing, "sendPasswordReset", true);
    this.$store
      .dispatch(`data/${DataModules.USERS}/sendResetPasswordEmail`, {
        user_id: this.userId
      })
      .then(() => {
        this.$buefy.toast.open({
          message: this.$t("_.reset_password_email_sent") as string
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.$delete(this.isProcessing, "sendPasswordReset");
      });
  }

  public confirmDisable2FA() {
    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          confirmButtonText: this.$t("_action.disable_2fa"),
          message: this.$t("_sentence.2fa_confirm_disable", {
            object: (this.$t("_.user") as string).toLowerCase()
          })
        },
        events: {
          confirmed: () => this.disable2FA()
        }
      }
    });
  }

  public disable2FA() {
    if (!this.$userCan("disable_user_2fa")) return;
    this.$set(this.isProcessing, "disable2FA", true);
    this.$store
      .dispatch(`data/${DataModules.USERS}/disable2FA`, {
        user_id: this.userId
      })
      .then(async () => {
        await this.$userMethods.reloadData();
        this.$buefy.toast.open({
          message: this.$t("_.2fa_disabled") as string
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.$delete(this.isProcessing, "disable2FA");
      });
  }

  public get canDisable2fa() {
    return this.$userCan("disable_user_2fa");
  }

  public get canView() {
    return this.$userCan("get_user");
  }

  public get canEdit() {
    return this.$userCan("update_user") && this.$store.state.user.admin;
  }
  public get canListUserGroups() {
    return this.$userCan("list_user_groups");
  }

  public get canSeeForm() {
    return this.canListUserGroups && this.canView;
  }

  public get canResendVerificationEmail() {
    return this.$userCan("resend_user_verification_email");
  }

  public get canResetPassword() {
    return this.$userCan("send_staff_password_reset_email");
  }

  public get isLoggedUser() {
    return this.userId === this.loggedUser.id;
  }

  public get thisUser() {
    return (
      this.$_.get(this.form, "name").trim() ||
      (this.$t("_.this_object", {
        object: this.$t("_.user")
      }) as string).toLowerCase()
    );
  }
}
